import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import moment from 'moment'
import {
  PurchaseRequestsDataCollectionDocument,
  type PurchaseRequestItemFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEmployeesFilter } from 'modules/employees/lib/filters/employees'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { contactFullName } from 'modules/finance/lib/contactsUtils'
import {
  getApprovalProcessStatus,
  getPurchaseRequestStatusLabel,
} from 'modules/procurement/components/purchaseRequests/SpendingRequestStatusLabel'
import { purchaseRequestDisplayTitle } from 'modules/procurement/lib/purchaseRequestTitle'

type StatusType = 'positive' | 'critical' | 'warning' | 'neutral' | 'info'

const mapPrStatusToDataCollection = (type: string): StatusType => {
  const mapping: Record<string, StatusType> = {
    positive: 'positive',
    critical: 'critical',
    warning: 'warning',
  }
  return mapping[type] ?? 'neutral'
}

const PurchaseRequestsDataTable = () => {
  const {
    company,
    access: { locale },
  } = useCurrentSession()
  const { navigation } = useNavigation()

  const approverFilter = useEmployeesFilter({
    label: i18n.t('procurement.purchase_requests.filter.approver'),
  })

  const source = useGraphqlDataCollectionSource({
    document: PurchaseRequestsDataCollectionDocument,
    perPage: 30,
    mapData: (data) =>
      data.procurement.purchaseRequestsConnection.edges.map((edge) => edge.node) ?? [],
    mapPagination: (data) => ({
      total: data.procurement.purchaseRequestsConnection.totalCount ?? 0,
    }),
    mapVariables: ({ sortings, filters, search }) => ({
      companyId: company.id,
      search: search ?? undefined,
      onlyMyPendingApprovals: filters.myPendingOnly?.[0] === 'true' || undefined,
      sortOrder: sortings?.[0] ?? { field: 'date', order: 'desc' },
      filter: {
        status: filters.status?.length ? { in: filters.status } : undefined,
        approversEmployeesIds: filters.approver?.length ? { in: filters.approver } : undefined,
        date:
          filters.date && typeof filters.date === 'object' && 'from' in filters.date
            ? {
                between: [
                  moment(filters.date.from).startOf('day').toISOString(),
                  moment(filters.date.to ?? filters.date.from)
                    .endOf('day')
                    .toISOString(),
                ],
              }
            : undefined,
      },
    }),
    search: { enabled: true },
    sortings: {
      description: { label: i18n.t('procurement.purchase_requests.list.description') },
      status: { label: i18n.t('procurement.purchase_requests.list.status') },
      date: { label: i18n.t('procurement.purchase_requests.list.requested') },
      deadline: { label: i18n.t('procurement.purchase_requests.list.deadline') },
    },
    defaultSortings: { field: 'date', order: 'desc' },
    filters: {
      approver: approverFilter,
      status: {
        label: i18n.t('procurement.purchase_requests.list.status'),
        type: 'in' as const,
        options: {
          options: [
            {
              label: i18n.t('procurement.purchase_requests.status.pending'),
              value: 'pending',
            },
            {
              label: i18n.t('procurement.purchase_requests.status.approved'),
              value: 'approved',
            },
            {
              label: i18n.t('procurement.purchase_requests.status.rejected'),
              value: 'rejected',
            },
          ],
        },
      },
      date: {
        label: i18n.t('procurement.purchase_requests.list.requested'),
        type: 'date' as const,
        options: {
          mode: 'range' as const,
          view: 'day' as const,
        },
      },
      myPendingOnly: {
        label: i18n.t('procurement.purchase_requests.tabs.pending_mine'),
        type: 'in' as const,
        hideSelector: true,
        options: {
          options: [{ label: 'true', value: 'true' }],
        },
      },
    },
    presets: [
      {
        label: i18n.t('procurement.purchase_requests.tabs.all'),
        filter: { myPendingOnly: [] },
      },
      {
        label: i18n.t('procurement.purchase_requests.tabs.pending_mine'),
        filter: { myPendingOnly: ['true'] },
      },
    ],
    itemUrl: (item: PurchaseRequestItemFragment) =>
      navigation.procurement.purchaseRequests.detail.fullPath({ id: item.id }),
  })

  return (
    <OneDataCollection
      id="procurement-purchase-requests/v1"
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            allowColumnHiding: true,
            columns: [
              {
                id: 'description',
                label: i18n.t('procurement.purchase_requests.list.description'),
                sorting: 'description',
                noHiding: true,
                render: (item: PurchaseRequestItemFragment) => ({
                  type: 'text' as const,
                  value: { text: purchaseRequestDisplayTitle(item) },
                }),
              },
              {
                id: 'status',
                label: i18n.t('procurement.purchase_requests.list.status'),
                sorting: 'status',
                render: (item: PurchaseRequestItemFragment) => {
                  const resolvedStatus = getApprovalProcessStatus({
                    status: item.status,
                    approvalProcess: item.approvalProcess,
                  })
                  const statusInfo = getPurchaseRequestStatusLabel(resolvedStatus)
                  return {
                    type: 'status' as const,
                    value: {
                      label: statusInfo.label,
                      status: mapPrStatusToDataCollection(statusInfo.type),
                    },
                  }
                },
              },
              {
                id: 'cost',
                label: i18n.t('procurement.purchase_requests.list.cost'),
                align: 'right' as const,
                render: (item: PurchaseRequestItemFragment) => ({
                  type: 'text' as const,
                  value: {
                    text: item.cost
                      ? MoneyWithCurrency({
                          amount: Number(item.cost.cents),
                          currency: item.cost.currency,
                          locale,
                        })
                      : '',
                  },
                }),
              },
              {
                id: 'date',
                label: i18n.t('procurement.purchase_requests.list.requested'),
                sorting: 'date',
                render: (item: PurchaseRequestItemFragment) => ({
                  type: 'text' as const,
                  value: { text: dateBackendDateTimeToUI(item.date) },
                }),
              },
              {
                id: 'deadline',
                label: i18n.t('procurement.purchase_requests.list.deadline'),
                sorting: 'deadline',
                render: (item: PurchaseRequestItemFragment) => ({
                  type: 'text' as const,
                  value: {
                    text: item.deadline ? dateBackendDateTimeToUI(item.deadline) : '-',
                  },
                }),
              },
              {
                id: 'requester',
                label: i18n.t('procurement.purchase_requests.list.employee'),
                render: (item: PurchaseRequestItemFragment) => ({
                  type: 'person' as const,
                  value: {
                    firstName: item.requesterEmployee?.fullName?.split(' ')[0] ?? '',
                    lastName: item.requesterEmployee?.fullName?.split(' ').slice(1).join(' ') ?? '',
                    src: buildUrl(item.requesterEmployee?.avatar?.url),
                  },
                }),
              },
              {
                id: 'vendor',
                label: i18n.t('procurement.purchase_requests.list.vendor'),
                render: (item: PurchaseRequestItemFragment) => ({
                  type: 'text' as const,
                  value: { text: contactFullName(item.vendor) },
                }),
              },
            ],
            frozenColumns: 1 as const,
          },
        },
      ]}
      emptyStates={{
        'no-data': {
          title: i18n.t('procurement.purchase_requests.empty.description_company'),
        },
      }}
    />
  )
}

export default PurchaseRequestsDataTable
